/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { ProductDetailsPage, ProductDetailsPageProvider, ShopProduct } from "@farmlands/commerce";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { PageContainer } from "../../../components/layout/PageContainer";
import { usePermission } from "../../../hooks/usePermission";
import { useThemeBackgroundColor } from "../../../hooks/useThemeBackgroundColor";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../testIds";
import { selectIsThisTfo } from "../../azure-b2c/storage";
import { CostPriceList } from "../../cost-price/components/CostPriceList";
import { StoreModalContainer } from "../../products/components/ProductDetails/StoreModalContainer";
import { PRODUCTS_CART_ROUTE } from "../../products/constants";
import { QuoteModalType } from "../../quotes/components/QuoteModal/types";
import QuoteProvider, { useQuoteModal } from "../../quotes/components/QuoteModal/useQuoteModal";
import { mapShopProductToProduct } from "../utils/map-shop-product-to-product";

const ShopProductDetailsPage = () => {
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isTfo = useSelector(selectIsThisTfo);

  const remoteConfig = useSelector(selectRemoteConfig);
  const hasCostPricePermission = usePermission("StaffSpecific_Pricing_CostPriceView").allowed;
  const navigate = useNavigate();
  const { masterProductId } = useParams();
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const handleToCheckout = () => {
    navigate(PRODUCTS_CART_ROUTE);
  };

  const selectedVariant = useMemo(
    () => ({
      size: searchParams.get("size") ?? "",
      colour: searchParams.get("colour") ?? "",
    }),
    [searchParams]
  );

  const handleVariantChange = (size: string, colour: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("size", size);
        params.set("colour", colour);
        return params;
      },
      { replace: true }
    );
  };

  const canAddToCart = usePermission("FarmlandsProShop_CartOrderCreate").allowed;
  const quoteProduct = useQuoteModal();
  const launchEnquiries = (product: ShopProduct) => {
    const mappedProduct = mapShopProductToProduct(product);

    quoteProduct.setQuoteType(QuoteModalType.ENQUIRY);
    quoteProduct.setProduct(mappedProduct);
  };

  const handleRequestAQuote = (shopProduct: ShopProduct) => {
    const mappedProduct = mapShopProductToProduct(shopProduct);
    quoteProduct.setQuoteType(QuoteModalType.QUOTE_SINGLE);
    quoteProduct.setProduct(mappedProduct);
  };

  const canRequestAQuote = quoteProduct.canRequestQuote && !isTfo;

  const additionalContent =
    remoteConfig.costPriceEnabled && hasCostPricePermission && masterProductId ? (
      <CostPriceList productId={parseInt(masterProductId)} />
    ) : null;

  return (
    <PageContainer data-testid={TestIds.pages.productDetails}>
      <ProductDetailsPageProvider
        showStoreModal={() => setShowStoreModal(true)}
        toCheckout={handleToCheckout}
        canAddToCart={canAddToCart}
        launchEnquiries={launchEnquiries}
        selectedVariant={selectedVariant}
        onVariantChange={handleVariantChange}
        canRequestAQuote={canRequestAQuote}
        requestAQuote={handleRequestAQuote}
        additionalContent={additionalContent}
      >
        <ProductDetailsPage />
      </ProductDetailsPageProvider>
      <StoreModalContainer open={showStoreModal} setOpen={setShowStoreModal} />
    </PageContainer>
  );
};

export default () => QuoteProvider(() => <ShopProductDetailsPage />);
